import { useEffect } from 'react';
import { useOrganizationSettingsQuery } from './useOrganizationSettingsQuery';
import { getCachedTheme, cacheTheme, resetCssVariables } from '@/lib/subdomain-utils';

/**
 * Convert hex color to HSL format for CSS variables
 */
const hexToHsl = (hex: string): string => {
  if (!hex || !hex.startsWith('#')) return '';
  
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number, s: number, l: number;

  l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: h = 0;
    }
    h /= 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

/**
 * Apply CSS variable to document root
 */
const applyCssVariable = (variableName: string, value: string) => {
  if (!value) return;
  document.documentElement.style.setProperty(variableName, value);
};

/**
 * Apply multiple CSS variables at once
 */
const applyCssVariables = (theme: Record<string, string>) => {
  Object.entries(theme).forEach(([key, value]) => {
    applyCssVariable(key, value);
  });
};

/**
 * Build theme object from settings array
 */
const buildThemeFromSettings = (
  settings: Array<{ key: string; value: string }>,
  globalPrimaryColor?: string
): Record<string, string> => {
  const theme: Record<string, string> = {};

  // Get settings values
  const getSetting = (key: string) => settings.find(s => s.key === key)?.value || '';

  // Color settings
  const primaryColor = getSetting('theme_primary_color') || globalPrimaryColor;
  const secondaryColor = getSetting('theme_secondary_color');
  const accentColor = getSetting('theme_accent_color');
  const warningColor = getSetting('theme_warning_color');
  const errorColor = getSetting('theme_error_color');

  if (primaryColor) {
    theme['--primary'] = hexToHsl(primaryColor);
  }
  if (secondaryColor) {
    theme['--secondary'] = hexToHsl(secondaryColor);
  }
  if (accentColor) {
    theme['--accent'] = hexToHsl(accentColor);
  }
  if (warningColor) {
    theme['--warning'] = hexToHsl(warningColor);
  }
  if (errorColor) {
    theme['--destructive'] = hexToHsl(errorColor);
  }

  // Layout settings
  const borderRadius = getSetting('theme_border_radius');
  const fontScale = getSetting('theme_font_scale');

  if (borderRadius) {
    const radiusMap: Record<string, string> = {
      'none': '0',
      'small': '0.375rem',
      'medium': '0.75rem',
      'large': '1rem',
      'full': '9999px'
    };
    theme['--radius'] = radiusMap[borderRadius] || '0.75rem';
  }

  if (fontScale) {
    const scaleMap: Record<string, string> = {
      'compact': '0.9',
      'normal': '1',
      'comfortable': '1.1'
    };
    theme['--font-scale'] = scaleMap[fontScale] || '1';
  }

  return theme;
};

interface UseThemeApplicatorOptions {
  organizationId: string | null | undefined;
  subdomain?: string;
  enabled?: boolean;
  globalPrimaryColor?: string;
}

/**
 * Single authoritative theme applicator
 * This is the ONLY hook that should apply CSS variables to the DOM
 * 
 * Usage: Call ONLY from OrganizationProvider or when explicitly previewing a theme
 */
export const useThemeApplicator = ({
  organizationId,
  subdomain,
  enabled = true,
  globalPrimaryColor,
}: UseThemeApplicatorOptions) => {
  const { settings, isLoading } = useOrganizationSettingsQuery(organizationId);

  useEffect(() => {
    if (!enabled || !organizationId) {
      console.log('[THEME-APPLICATOR] Skipping - disabled or no org ID');
      return;
    }

    console.log('[THEME-APPLICATOR] Applying theme for org:', organizationId, {
      settingsCount: settings.length,
      isLoading,
    });

    // Step 1: Try to apply cached theme immediately for instant load
    const cachedTheme = getCachedTheme(organizationId);
    if (cachedTheme && isLoading) {
      console.log('[THEME-APPLICATOR] Using cached theme (loading)');
      applyCssVariables(cachedTheme);
      return;
    }

    // Step 2: Apply fresh theme from settings
    if (!isLoading && settings.length >= 0) {
      const theme = buildThemeFromSettings(settings, globalPrimaryColor);
      
      if (Object.keys(theme).length > 0) {
        console.log('[THEME-APPLICATOR] Applying fresh theme:', theme);
        applyCssVariables(theme);
        
        // Cache for next time
        cacheTheme(organizationId, theme);
        console.log('[THEME-APPLICATOR] Cached theme for org:', organizationId);
      } else if (globalPrimaryColor) {
        // Fallback to global primary only
        console.log('[THEME-APPLICATOR] Using global primary color fallback');
        const fallbackTheme = { '--primary': hexToHsl(globalPrimaryColor) };
        applyCssVariables(fallbackTheme);
      }
    }

    // Cleanup: Reset CSS variables when unmounting or org changes
    return () => {
      console.log('[THEME-APPLICATOR] Cleanup - resetting CSS variables for org:', organizationId);
      resetCssVariables();
    };
  }, [organizationId, settings, isLoading, enabled, globalPrimaryColor, subdomain]);

  return {
    isApplying: isLoading,
  };
};
